import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { OtaPreview } from '@/api/ota/types';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { QueryConfig } from '@/common/react-query';
import { APIResponse } from '@/common/types';
import { useWavelink } from '@/components/providers/WavelinkProvider';
import { OTA_PREVIEW_QUERY_KEY, QUERIES_REFETCH_INTERVAL } from '@/config';
import { useOptionalSpotterContext } from '@/context/SpotterContext';

export type GetDeviceOtaPreviewResponse = APIResponse<OtaPreview>;

const getDeviceOtaPreview = async (
    { spotterId }: { spotterId: string },
    options?: RequestOptions
): Promise<OtaPreview> => {
    const response = await axios.get<GetDeviceOtaPreviewResponse, GetDeviceOtaPreviewResponse>(
        `/user-rest/devices/${spotterId}/ota/preview`,
        requestConfig(options)
    );

    return response.data;
};

export type UseDeviceOtaPreviewOptions = {
    spotterId?: string | null;
    config?: QueryConfig<typeof getDeviceOtaPreview>;
};

/**
 * The version a device would be updated to, and the steps that would get it there.
 *
 * Takes `spotterId` from the argument, or from SpotterContext when the argument is omitted. Pass
 * `spotterId: null` rather than omitting it while an explicit id is still loading, so a surrounding
 * context is not picked up by accident.
 */
export const useDeviceOtaPreview = ({
    spotterId: spotterIdProp,
    config: { enabled = true, ...config } = {},
}: UseDeviceOtaPreviewOptions = {}) => {
    const { spotterId } = useOptionalSpotterContext({ spotterId: spotterIdProp });
    const { isQueryCovered } = useWavelink();

    // The prefix and the spotterId are their own segments, which is the shape wavelink keys an
    // invalidation message by and the shape `isQueryCovered` compares against.
    const queryKey = useMemo(() => [OTA_PREVIEW_QUERY_KEY, spotterId], [spotterId]);
    const isCoveredByWavelink = isQueryCovered(queryKey);

    return useQuery({
        queryKey,
        // `enabled` below proves spotterId is set before queryFn runs, which the compiler cannot follow.
        queryFn: ({ signal }) => getDeviceOtaPreview({ spotterId: spotterId! }, { signal }),
        enabled: !!spotterId && enabled,
        staleTime: 1000 * 60 * 5, // 5 minutes
        refetchInterval: isCoveredByWavelink ? false : QUERIES_REFETCH_INTERVAL,
        ...config,
    });
};
